import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getGscOverview, refreshPerformance, runInspectionBatch, type Inspection } from "@/lib/gsc.functions";

export const Route = createFileRoute("/admin/search-console")({
  head: () => ({ meta: [{ title: "Google Search Console · Yönetim" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: SearchConsolePage,
});

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString("tr-TR", { timeZone: "Europe/Istanbul", dateStyle: "medium", timeStyle: "short" }) : "—");
const short = (u: string | null) => (u ? u.replace(/^https:\/\/akillisurdurulebilirlik\.com/, "") || "/" : "—");

const COVERAGE_TR: Record<string, string> = {
  "Submitted and indexed": "Gönderildi ve dizinde",
  "Indexed, not submitted in sitemap": "Dizinde (haritada bildirilmemiş)",
  "Crawled - currently not indexed": "Tarandı, şu an dizinde değil",
  "Discovered - currently not indexed": "Keşfedildi, şu an dizinde değil",
  "URL is unknown to Google": "Google bu adresi bilmiyor",
  "Duplicate, Google chose different canonical than user": "Kopya: Google farklı canonical seçti",
  "Alternate page with proper canonical tag": "Alternatif sayfa (doğru canonical)",
  "Page with redirect": "Yönlendirmeli sayfa",
  "Excluded by ‘noindex’ tag": "noindex ile hariç",
  "Not found (404)": "Bulunamadı (404)",
};
const tr = (s: string | null) => (s ? COVERAGE_TR[s] ?? s : "—");

function canonicalMismatch(r: Inspection) {
  return !!r.google_canonical && r.google_canonical !== r.url;
}
function isIndexed(r: Inspection) {
  return r.verdict === "PASS";
}

function SearchConsolePage() {
  const qc = useQueryClient();
  const fetchOverview = useServerFn(getGscOverview);
  const inspect = useServerFn(runInspectionBatch);
  const perf = useServerFn(refreshPerformance);
  const { data, isLoading, error } = useQuery({ queryKey: ["gsc-overview"], queryFn: () => fetchOverview() });
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "not" | "canon" | "err">("all");

  const rows = data?.inspections ?? [];
  const stats = useMemo(() => {
    const ok = rows.filter((r) => !r.error);
    return {
      inspected: ok.length,
      indexed: ok.filter(isIndexed).length,
      notIndexed: ok.filter((r) => !isIndexed(r)).length,
      canon: ok.filter(canonicalMismatch).length,
      errors: rows.filter((r) => r.error).length,
    };
  }, [rows]);

  const list = rows.filter((r) =>
    filter === "all" ? true : filter === "not" ? !r.error && !isIndexed(r) : filter === "canon" ? canonicalMismatch(r) : !!r.error,
  );
  const priority = rows.filter((r) => !r.error && !isIndexed(r) && ["Yazı", "Tema", "Kitap", "Ana sayfa"].includes(r.page_type));

  async function runAll() {
    setBusy("Denetleniyor…");
    setNote(null);
    try {
      for (let i = 0; i < 20; i++) {
        const r = await inspect();
        setBusy(`Denetleniyor… kalan ${r.remaining}`);
        await qc.invalidateQueries({ queryKey: ["gsc-overview"] });
        if (r.message) {
          setNote(r.message);
          break;
        }
        if (r.remaining <= 0 || r.done + r.failed === 0) break;
      }
    } catch (e) {
      setNote(e instanceof Error ? e.message : "Denetim çalışmadı.");
    } finally {
      setBusy(null);
    }
  }
  async function runPerf() {
    setBusy("Performans alınıyor…");
    setNote(null);
    try {
      const r = await perf();
      if (r.skipped) setNote("Performans verisi son 20 saat içinde alındı; yeniden sorgulanmadı.");
      await qc.invalidateQueries({ queryKey: ["gsc-overview"] });
    } catch (e) {
      setNote(e instanceof Error ? e.message : "Performans alınamadı.");
    } finally {
      setBusy(null);
    }
  }

  const p = data?.performance as null | {
    period_start: string; period_end: string; fetched_at: string;
    totals: { clicks: number; impressions: number; ctr: number; position: number };
    top_pages: { key: string; clicks: number; impressions: number; position: number }[];
    top_queries: { key: string; clicks: number; impressions: number; position: number }[];
  };

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <h1 className="text-2xl font-semibold">Google Search Console</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Mülk: <strong>{data?.property ?? "—"}</strong> · yalnız okuma. Google verisi ile sitenin kendi teknik bilgisi ayrı sütunlarda gösterilir.
      </p>
      <p className="mt-2 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
        Yalnız site haritasındaki {data?.sitemapCount ?? "…"} adres Google'a tek tek soruluyor. Bu liste, Search Console'daki bütün hariç tutulan adreslerin eksiksiz listesi değildir. Google'ın dizine almaması tek başına teknik hata anlamına gelmez; Google verisi birkaç gün gecikmeli olabilir.
      </p>

      {isLoading && <p className="mt-6 text-sm">Yükleniyor…</p>}
      {error && <p className="mt-6 text-sm text-destructive">{error instanceof Error ? error.message : "Veriler yüklenemedi."}</p>}

      {data && (
        <>
          <div className="mt-5 flex flex-wrap gap-2">
            <button disabled={!!busy || data.pending === 0} onClick={runAll} className="min-h-10 rounded-md border border-border px-4 text-sm hover:border-foreground disabled:opacity-50">
              {data.pending ? `Sıradaki adresleri denetle (${data.pending})` : "Denetlenecek adres yok"}
            </button>
            <button disabled={!!busy} onClick={runPerf} className="min-h-10 rounded-md border border-border px-4 text-sm hover:border-foreground disabled:opacity-50">
              Arama performansını güncelle
            </button>
            {busy && <span className="self-center text-sm text-muted-foreground">{busy}</span>}
          </div>
          {note && <p className="mt-2 text-sm text-destructive">{note}</p>}

          <section className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
            {[
              ["Denetlenen", stats.inspected],
              ["Dizinde", stats.indexed],
              ["Dizinde değil", stats.notIndexed],
              ["Canonical farkı", stats.canon],
              ["Başarısız sorgu", stats.errors],
            ].map(([k, v]) => (
              <div key={k as string} className="rounded-md border border-border p-3">
                <div className="text-xs text-muted-foreground">{k}</div>
                <div className="mt-1 text-2xl font-semibold">{v}</div>
              </div>
            ))}
          </section>
          <p className="mt-2 text-xs text-muted-foreground">
            Son veri güncellemesi: {fmt(data.lastUpdate)} · Son 24 saatte {data.quota.inspectionsLast24h}/{data.quota.cap} adres sorgusu, {data.quota.failedLast24h} başarısız çağrı
          </p>

          {priority.length > 0 && (
            <section className="mt-8">
              <h2 className="text-lg font-semibold">Dizinde olmayan önemli sayfalar</h2>
              <ul className="mt-2 divide-y divide-border rounded-md border border-border text-sm">
                {priority.map((r) => (
                  <li key={r.url} className="flex flex-wrap justify-between gap-2 p-3">
                    <a href={r.url} target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline">{short(r.url)}</a>
                    <span className="text-muted-foreground">{r.page_type} · {tr(r.coverage_state)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="mt-8">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-semibold">Adres bazında durum</h2>
              <div className="flex flex-wrap gap-1 text-xs">
                {([["all", "Tümü"], ["not", "Dizinde değil"], ["canon", "Canonical farkı"], ["err", "Başarısız"]] as const).map(([k, l]) => (
                  <button key={k} onClick={() => setFilter(k)} aria-pressed={filter === k} className={`min-h-9 rounded-full border px-3 ${filter === k ? "border-foreground bg-foreground text-background" : "border-border"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="mt-3 overflow-x-auto rounded-md border border-border">
              <table className="w-full min-w-[760px] text-left text-xs">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="p-2" rowSpan={2}>Adres</th>
                    <th className="p-2 text-center" colSpan={4}>Google verisi</th>
                    <th className="p-2 text-center" colSpan={2}>Site</th>
                  </tr>
                  <tr>
                    <th className="p-2">Dizin durumu</th>
                    <th className="p-2">Google canonical</th>
                    <th className="p-2">Son tarama</th>
                    <th className="p-2">Getirme</th>
                    <th className="p-2">Bildirilen canonical</th>
                    <th className="p-2">Tür</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {list.map((r) => (
                    <tr key={r.url}>
                      <td className="p-2"><a href={r.url} target="_blank" rel="noreferrer" className="hover:underline">{short(r.url)}</a></td>
                      <td className="p-2">{r.error ? <span className="text-destructive">Sorgu başarısız: {r.error}</span> : tr(r.coverage_state)}</td>
                      <td className={`p-2 ${canonicalMismatch(r) ? "font-medium text-destructive" : ""}`}>{r.error ? "—" : short(r.google_canonical)}</td>
                      <td className="p-2">{r.error ? "—" : fmt(r.last_crawl_time)}</td>
                      <td className="p-2">{r.page_fetch_state ?? "—"}</td>
                      <td className="p-2">{r.error ? "—" : short(r.user_canonical)}</td>
                      <td className="p-2">{r.page_type}</td>
                    </tr>
                  ))}
                  {list.length === 0 && <tr><td className="p-3 text-muted-foreground" colSpan={7}>Kayıt yok.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mt-8">
            <h2 className="text-lg font-semibold">Arama performansı</h2>
            {!p ? (
              <p className="mt-2 text-sm text-muted-foreground">Henüz performans verisi alınmadı.</p>
            ) : (
              <>
                <p className="mt-1 text-xs text-muted-foreground">{p.period_start} – {p.period_end} · alındı {fmt(p.fetched_at)}</p>
                <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {[
                    ["Tıklama", p.totals.clicks.toLocaleString("tr-TR")],
                    ["Gösterim", p.totals.impressions.toLocaleString("tr-TR")],
                    ["Tıklama oranı", `%${(p.totals.ctr * 100).toFixed(1)}`],
                    ["Ortalama konum", p.totals.position.toFixed(1)],
                  ].map(([k, v]) => (
                    <div key={k} className="rounded-md border border-border p-3"><div className="text-xs text-muted-foreground">{k}</div><div className="mt-1 text-xl font-semibold">{v}</div></div>
                  ))}
                </div>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  {([["En çok tıklanan sayfalar", p.top_pages, true], ["En çok gösterim alan sorgular", [...p.top_queries].sort((a, b) => b.impressions - a.impressions), false]] as const).map(([title, items, isPage]) => (
                    <div key={title}>
                      <h3 className="text-sm font-medium">{title}</h3>
                      <ul className="mt-2 divide-y divide-border rounded-md border border-border text-xs">
                        {items.map((it) => (
                          <li key={it.key} className="flex justify-between gap-3 p-2">
                            <span className="min-w-0 truncate">{isPage ? short(it.key) : it.key}</span>
                            <span className="shrink-0 text-muted-foreground">{it.clicks} tık · {it.impressions} gös. · {it.position.toFixed(1)}</span>
                          </li>
                        ))}
                        {items.length === 0 && <li className="p-2 text-muted-foreground">Veri yok.</li>}
                      </ul>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}
